import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { icons } from "../../../../../../../../utils/icons";
import { useTranslation } from "react-i18next";
import { VIEW_TYPES } from "../../types/ViewTypes";
import { useEvidences } from "../../../../../../../../hooks/evidences/useEvidences";
import EvidenceDetail from "../../../../../../../actionPlans/pages/form/components/evidenceDetail";
import StarIcon from '@mui/icons-material/Star';
import { ActionDrawer } from "../../../../../../../../components/ActionDrawer";
import { useEvidenceDownload } from "../../../../../../../calendar/pages/detail/hooks";
import { SeeOneDetailProps } from "./types";
import { Status } from "../../../../../../../../utils/enums";
import { Constants } from "../../../../../../../../utils/constants";
import { format } from "date-fns";
import es from "date-fns/locale/es";
import { useStyles } from "./styles";

export const SeeOneDetail = ({ onAttend, data }: SeeOneDetailProps) => {
    const styles = useStyles();
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();

    const isSolutionRejected = data?.statusIdAnswer === Status.REJECTED;
    const isSolutionApproved = data?.statusIdAnswer === Status.APPROVED;
    const solutionComment = data?.descriptionAnswer;
    const commentApprovalSolution = data?.commentApprovalAnswer;
    const reasonOfRejectionAnswer = data?.reasonOfRejectionAnswer;
    const rating = data?.rating;
    const formattedDate = data?.dateUploadSolution
        ? format(new Date(data.dateUploadSolution), Constants.dateFormatGeneral3, { locale: es })
        : t("reportFolio.detail.general.noDateAvailable")
        ;

    const formattedDateAnswer = data?.dateRequestManagement
        ? format(new Date(data.dateRequestManagement), Constants.dateFormatGeneral3, { locale: es })
        : t("reportFolio.detail.general.noDateAvailable");

    const SendBy = data?.userUploadAnswer
        ? `${data.userUploadAnswer.firstName} ${data.userUploadAnswer.lastName}`
        : t("reportFolio.detail.general.noAssignedUser");

    const ResponseBy = data?.userReviewerAnswer
        ? `${data.userReviewerAnswer.firstName} ${data.userReviewerAnswer.lastName}`
        : t("reportFolio.detail.general.noAssignedUser");

    const { evidences, existEvidences } = useEvidences({
        evidencesResponse: data?.evidencesAnswer,
        manualValue: false,
    });

    const { disable: isDownloading, downloadEvidences, } = useEvidenceDownload(
        'evidencias',
        data?.evidencesAnswer
    );

    const { evidences: evidencesSolution, existEvidences: existEvidencesSolution } = useEvidences({
        evidencesResponse: isSolutionRejected
            ? data?.evidencesRejectionAnswer
            : data?.evidencesApprovalAnswer,
        manualValue: false,
    });

    const { disable: isDownloadingSolution, downloadEvidences: downloadSolution, } = useEvidenceDownload(
        'evidencias',
        isSolutionRejected ? data?.evidencesRejectionAnswer : data?.evidencesApprovalAnswer
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography sx={styles.title} fontWeight={'bold'}>
                {t("reportFolio.detail.general.solutionDetailPosition", {position:data?.position})}
            </Typography>

            {!data?.isManagement && !isSolutionApproved && !isSolutionRejected && <Typography variant="body1" sx={styles.subTitle}>
                {data?.isIntructionComment ? t("reportFolio.detail.general.solutionIntructionSendInfo") : t("reportFolio.detail.general.solutionSendInfo")}
            </Typography>}

            <Grid container>
                <Grid item xs={12} sm={12} md={12} lg={isSolutionApproved || isSolutionRejected ? 6 : 12} xl={isSolutionApproved || isSolutionRejected ? 6 : 12} sx={styles.grid} className="brdLft">
                    <Box>
                        <Typography variant="body2" fontWeight={'bold'}>
                            {t("reportFolio.detail.general.solutionSent")}
                        </Typography>

                        <Typography variant="body2" color="textSecondary"
                            sx={styles.desbord}
                        >
                            {solutionComment ?? "No hay comentario."}
                        </Typography>
                    </Box>

                    <Box sx={styles.informationAndEvidences}>
                        <Box>
                            <Typography variant="body2" fontWeight={'bold'}>{t("reportFolio.detail.general.information")}</Typography>
                            <Typography variant="body2" color="textSecondary">{t("reportFolio.detail.general.generatedOn", { fecha: formattedDate })}</Typography>
                            <Typography variant="body2" color="textSecondary">{t("reportFolio.detail.general.generatedBy", { provider: SendBy })}</Typography>
                        </Box>

                        <Box>
                            <Typography variant="body2" fontWeight={'bold'}>{t("reportFolio.detail.general.evidencesDetail")}</Typography>
                            <ActionDrawer
                                title="EVIDENCIAS"
                                body={
                                    <Box sx={styles.mainContainer}>
                                        <Box sx={styles.bodyContainer}>
                                            <EvidenceDetail
                                                existEvidences={existEvidences}
                                                evidences={{
                                                    image: evidences?.images,
                                                    comment: evidences?.comments,
                                                    documents: evidences?.documents,
                                                    video: evidences?.video,
                                                    sign: evidences?.sign
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            <Button variant='text_primary_small' onClick={downloadEvidences} disabled={isDownloading}>{t('schedule.buttons.downloadEvidence')}</Button>
                                        </Box>
                                    </Box>
                                }
                            >
                                {
                                    ({ openDrawer }) =>
                                        <Tooltip title="">
                                            <IconButton
                                                onClick={() => { openDrawer() }}
                                                sx={styles.clip}
                                                disabled={!existEvidences}
                                            >
                                                {icons("clip")}
                                            </IconButton>
                                        </Tooltip>
                                }
                            </ActionDrawer>
                        </Box>

                    </Box>
                </Grid>

                {(isSolutionApproved || isSolutionRejected) &&
                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} sx={styles.grid}>
                        <Box>
                            <Typography variant="body2" fontWeight={'bold'} sx={styles.desbord}>
                                {isSolutionRejected
                                    ? t("reportFolio.detail.general.rejected")
                                    : t("reportFolio.detail.general.approved")}
                            </Typography>
                            {isSolutionRejected ? (
                                <Typography variant="body2" color="textSecondary" sx={styles.desbord} >
                                    {reasonOfRejectionAnswer}
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="textSecondary" sx={styles.desbord} >
                                    {commentApprovalSolution}
                                </Typography>
                            )
                            }

                        </Box>
                        <Box sx={styles.informationAndEvidences}>
                            <Box>
                                <Typography variant="body2" fontWeight={'bold'}>{t("reportFolio.detail.general.information")}</Typography>
                                <Typography variant="body2" color="textSecondary">{t("reportFolio.detail.general.generatedOn", { fecha: formattedDateAnswer })}</Typography>
                                <Typography variant="body2" color="textSecondary">{t("reportFolio.detail.general.generatedBy", { provider: ResponseBy })}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" fontWeight={'bold'}>{t("reportFolio.detail.general.evidencesDetail")}</Typography>
                                <ActionDrawer
                                    title="EVIDENCIAS"
                                    body={
                                        <Box sx={styles.mainContainer}>
                                            <Box sx={styles.bodyContainer}>
                                                <EvidenceDetail
                                                    existEvidences={existEvidencesSolution}
                                                    evidences={{
                                                        image: evidencesSolution?.images,
                                                        comment: evidencesSolution?.comments,
                                                        documents: evidencesSolution?.documents,
                                                        video: evidencesSolution?.video,
                                                        sign: evidencesSolution?.sign
                                                    }}
                                                />
                                            </Box>
                                            <Box>
                                                <Button variant='text_primary_small' onClick={downloadSolution} disabled={isDownloadingSolution}>{t('schedule.buttons.downloadEvidence')}</Button>
                                            </Box>
                                        </Box>
                                    }
                                >
                                    {
                                        ({ openDrawer }) =>
                                            <Tooltip title="">
                                                <IconButton
                                                    onClick={() => { openDrawer() }}
                                                    sx={styles.clip}
                                                    disabled={!existEvidencesSolution}
                                                >
                                                    {icons("clip")}
                                                </IconButton>
                                            </Tooltip>
                                    }
                                </ActionDrawer>
                            </Box>

                        </Box>
                        {rating?.rating &&
                            <Box>
                                <Box>
                                    <Typography variant="body2" fontWeight={'bold'}>{t("reportFolio.detail.general.survey")}</Typography>
                                    <Box display="flex" flexDirection="column">
                                        {rating?.rating && (
                                            <>
                                                <Typography variant="body1" color="textSecondary">
                                                    {rating.ratingQuestion}
                                                </Typography>

                                                <Box display="flex">
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <Typography
                                                            key={star}
                                                            component="span"
                                                            sx={{
                                                                color: star <= rating.rating ? '#FFD700' : '#E0E0E0',
                                                                fontSize: '24px'
                                                            }}
                                                        >
                                                            <StarIcon />
                                                        </Typography>
                                                    ))}
                                                </Box>

                                                {rating.ratingComment && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ '&.MuiTypography-root': { wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal", }, }}
                                                    >
                                                        {rating.ratingComment}
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        }
                    </Grid>
                }
            </Grid>

            <Box sx={styles.button}>
                <Button onClick={() => { onAttend(VIEW_TYPES.TAB_SWITCHER) }}>{t("general.cancel")}</Button>
            </Box>

        </Box >
    );
};
